import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../context/AuthContext";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
    TrendingUp, Users, Target, Calendar,
    Eye, AlertCircle,
    CheckCircle2, Clock, RefreshCw,
    Flame, Zap, Snowflake, DollarSign, BarChart2, ChevronRight, Activity,
    TrendingDown, Award, Mail, MousePointer, Reply, FileText, Send,
    BadgeCheck, IndianRupee, CalendarClock, RotateCcw, ArrowRight, LayoutGrid, Inbox,
} from "lucide-react";
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import useApiMethods from "../../lib/useApiMethods";
import { DASHBOARD_SUMMARY, SALES_FORECAST_URL, DASHBOARD_ENGAGEMENT_URL, CONVERSION_ANALYTICS_URL, EMAIL_ANALYTICS_URL, ONBOARDED_REVENUE_REPORT, ONBOARDED_AMC_EXPIRY_URL, PROJECTIONS_SUMMARY_URL } from "../../constants/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import ToastMsg from "../../components/common/ToastMsg";
import { Skeleton } from "../../components/ui/skeleton";
import { KpiCard, PipelineBar, STAGE_COLORS, formatINR, chartColor } from "../../components/dashboard/shared";
import RepDashboard from "./rep/RepDashboard";
import TeamToday from "./team/TeamToday";

dayjs.extend(relativeTime);

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ── layout pieces ──────────────────────────────────────────────────────────────

// One dashboard block: a white card with an icon, a title, a one-line
// subtitle and an optional action on the right.
const Section = ({ id, icon: Icon, title, subtitle, action, children, className }) => (
    <section
        id={id}
        className={cn(
            "scroll-mt-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-5",
            className
        )}
    >
        <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3 min-w-0">
                {Icon && (
                    <div className="w-9 h-9 rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4 w-4" />
                    </div>
                )}
                <div className="min-w-0">
                    <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">{title}</h2>
                    {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
                </div>
            </div>
            {action}
        </header>
        {children}
    </section>
);

// A sub-block inside a Section.
const Panel = ({ icon: Icon, iconClass = "text-brand-500", title, right, children, className }) => (
    <div className={cn("rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-900/30 p-4 min-w-0", className)}>
        {(title || right) && (
            <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    {Icon && <Icon className={cn("h-3.5 w-3.5", iconClass)} />} {title}
                </p>
                {right}
            </div>
        )}
        {children}
    </div>
);

const LinkAction = ({ label = "Full report", onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="group inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200 rounded-lg px-2 py-1 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors"
    >
        {label} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
    </button>
);

const Empty = ({ text = "No data yet", className }) => (
    <div className={cn("flex flex-col items-center justify-center gap-2 py-8 text-center", className)}>
        <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center">
            <Inbox className="h-4 w-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500">{text}</p>
    </div>
);

const initials = (name) =>
    (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();

// ── sub-components ─────────────────────────────────────────────────────────────

const ActivityItem = ({ item, onNavigate }) => (
    <div
        className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
        onClick={() => item.customer_id && onNavigate(item.customer_id)}
    >
        <div className="w-9 h-9 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
            {initials(item.customer_name)}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {item.customer_name}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                <span className="truncate">{item.from_status || "New"}</span>
                <ChevronRight className="h-3 w-3 flex-shrink-0 text-gray-300" />
                <span className="inline-flex items-center gap-1 font-medium text-gray-700 dark:text-gray-200 truncate">
                    <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", STAGE_COLORS[item.to_status] || "bg-gray-400")} />
                    {item.to_status}
                </span>
                {item.user_name && <span className="truncate hidden sm:inline">· {item.user_name}</span>}
            </div>
        </div>
        <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
            {item.created_date ? dayjs(item.created_date).fromNow() : ''}
        </span>
    </div>
);

const RANK_STYLES = [
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
];

const PerformerRow = ({ p, rank }) => (
    <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <div className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
            RANK_STYLES[rank - 1] || "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
        )}>
            {rank}
        </div>
        <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.name || p.emp_code}</p>
                <span className={cn(
                    "text-xs font-semibold tabular-nums flex-shrink-0",
                    p.conversion_rate >= 50 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'
                )}>
                    {p.conversion_rate}%
                </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full bg-brand-500" style={{ width: `${Math.min(Number(p.conversion_rate) || 0, 100)}%` }} />
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                {p.total_leads} leads · {p.closed_won} won
            </p>
        </div>
    </div>
);

// Hot / medium / cold as one stacked bar with a legend.
const PotentialBar = ({ hot = 0, medium = 0, cold = 0 }) => {
    const total = hot + medium + cold;
    const parts = [
        { label: "Hot", value: hot, icon: Flame, bar: "bg-red-500", text: "text-red-600 dark:text-red-400" },
        { label: "Medium", value: medium, icon: Zap, bar: "bg-amber-400", text: "text-amber-600 dark:text-amber-400" },
        { label: "Cold", value: cold, icon: Snowflake, bar: "bg-sky-400", text: "text-sky-600 dark:text-sky-400" },
    ];
    return (
        <div>
            <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 gap-0.5">
                {total > 0 && parts.map(p => p.value > 0 && (
                    <div key={p.label} className={p.bar} style={{ width: `${(p.value / total) * 100}%` }} />
                ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
                {parts.map(({ label, value, icon: Icon, text }) => (
                    <div key={label}>
                        <p className={cn("text-xs font-medium flex items-center gap-1", text)}>
                            <Icon className="h-3 w-3" /> {label}
                        </p>
                        <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white mt-0.5">{value.toLocaleString()}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MiniStat = ({ label, value, tone = "text-gray-900 dark:text-white" }) => (
    <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/60 px-3 py-2">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
        <p className={cn("text-base font-semibold tabular-nums mt-0.5", tone)}>{(value ?? 0).toLocaleString()}</p>
    </div>
);

const RateBar = ({ icon: Icon, label, rate, done, total, verb, bar, text }) => (
    <Panel>
        <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                <Icon className={cn("h-3.5 w-3.5", text)} /> {label}
            </span>
            <span className={cn("text-sm font-semibold tabular-nums", text)}>{rate}%</span>
        </div>
        <div className="h-2 bg-gray-200/70 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${Math.min(Number(rate) || 0, 100)}%` }} />
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
            {done} of {total} emails {verb}
        </p>
    </Panel>
);

// ── helpers ────────────────────────────────────────────────────────────────────

const fmtMonth = (ym) => {
    if (!ym) return "";
    const [year, month] = ym.split("-");
    return new Date(Number(year), Number(month) - 1, 1)
        .toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const firstName = () => {
    try {
        const p = JSON.parse(sessionStorage.getItem("profile") || "{}");
        return p.login_fname || sessionStorage.getItem("user_name") || "";
    } catch {
        return sessionStorage.getItem("user_name") || "";
    }
};

const FORECAST_SCENARIOS = [
    { key: "worst_case",  label: "Worst" },
    { key: "most_likely", label: "Likely" },
    { key: "best_case",   label: "Best" },
];

// Brand violet from light to deep, ending green for the won stage.
const FUNNEL_GRADIENTS = [
    ["#d0b2f7", "#e4d4fb"], ["#bb85f1", "#d0b2f7"], ["#a95aec", "#bb85f1"],
    ["#8a45e6", "#a95aec"], ["#7333c9", "#8a45e6"], ["#5d29a3", "#7333c9"],
    ["#40187a", "#5d29a3"], ["#16a34a", "#4ade80"],
];

const DashFunnel = ({ data }) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className="space-y-1.5">
            {data.map((item, idx) => {
                const w = Math.max((item.count / maxCount) * 100, 8);
                const [from, to] = FUNNEL_GRADIENTS[idx] || ["#6b7280", "#9ca3af"];
                const light = idx < 2;
                return (
                    <div key={item.stage} className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 w-24 text-right shrink-0 truncate">{item.stage}</span>
                        <div className="flex-1 flex justify-center">
                            <div
                                className="h-7 rounded-md flex items-center justify-between px-2.5 transition-all"
                                style={{ width: `${w}%`, minWidth: "80px", background: `linear-gradient(90deg,${from},${to})` }}
                            >
                                <span className={cn("text-xs font-semibold tabular-nums", light ? "text-brand-900" : "text-white")}>{item.count}</span>
                                <span className={cn("text-[10px]", light ? "text-brand-800/70" : "text-white/80")}>{Math.round((item.count / maxCount) * 100)}%</span>
                            </div>
                        </div>
                        <div className="w-12 shrink-0 text-[10px] tabular-nums">
                            {item.drop_pct != null && <span className="text-red-500">↓{item.drop_pct}%</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Shared look for every chart on the page: no box borders, faint grid lines,
// small grey ticks, rounded tooltip.
const GRID = "rgba(148, 163, 184, 0.15)";
const TICKS = { color: "#94a3b8", font: { size: 11 } };
const TOOLTIP = { backgroundColor: "#1e0059", padding: 10, cornerRadius: 8, titleFont: { size: 12 }, bodyFont: { size: 12 } };
const LEGEND = { position: "top", align: "end", labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: "rectRounded", font: { size: 11 }, color: "#64748b" } };
const axis = (extra = {}) => ({ border: { display: false }, grid: { color: GRID }, ticks: TICKS, ...extra });
const flatAxis = (extra = {}) => ({ border: { display: false }, grid: { display: false }, ticks: TICKS, ...extra });

// ── main component ─────────────────────────────────────────────────────────────

// A sales rep (role_type 'user') gets their own "My Day" dashboard; managers
// and admins keep the team view below. Branching in a wrapper rather than
// returning early inside ManagerDashboard, which would break the hooks rules.
const Dashboard = () => (
    sessionStorage.getItem("login_role") === "user" ? <RepDashboard /> : <ManagerDashboard />
);

const ManagerDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    // hasPermission as well as hasFeature: the plan can have a feature switched
    // on while this role has no permission for it. Calling anyway returned 403,
    // and the global interceptor turned that into a "you don't have permission"
    // toast on a page the user is entitled to see.
    const { hasFeature, hasPermission } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshCount, setRefreshCount] = useState(0);
    const [forecastData,     setForecastData]    = useState(null);
    const [engagementData,  setEngagementData]  = useState(null);
    const [conversionData,  setConversionData]  = useState(null);
    const [emailSummary,    setEmailSummary]    = useState({ total_sent: 0, total_opened: 0, total_clicked: 0, total_replied: 0, total_bounced: 0, open_rate: 0, click_rate: 0, user_breakdown: [], template_performance: [] });
    const [forecastScenario, setForecastScenario] = useState("most_likely");
    const [projSummary,      setProjSummary]      = useState(null);
    const [onboardedSummary, setOnboardedSummary] = useState(null);
    const [expiringAmc,      setExpiringAmc]      = useState([]);

    useEffect(() => {
        setLoading(true);
        fetch(DASHBOARD_SUMMARY)
            .then(res => {
                if (res?.status === 'success') setData(res.data);
                else ToastMsg('error', res?.message || 'Failed to load dashboard');
            })
            .catch(() => ToastMsg('error', 'Failed to load dashboard'))
            .finally(() => setLoading(false));
    }, [refreshCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetch(DASHBOARD_ENGAGEMENT_URL)
            .then(res => { if (res?.status === 'success') setEngagementData(res.data); })
            .catch(() => {});
    }, [refreshCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetch(`${SALES_FORECAST_URL}?scenario=${forecastScenario}`)
            .then(res => { if (res?.status === 'success') setForecastData(res.data); })
            .catch(() => {});
        fetch(PROJECTIONS_SUMMARY_URL)
            .then(res => { if (res?.status === 'success') setProjSummary(res.data); })
            .catch(() => {});
    }, [refreshCount, forecastScenario]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetch(CONVERSION_ANALYTICS_URL)
            .then(res => { if (res?.status === 'success') setConversionData(res.data); })
            .catch(() => {});
    }, [refreshCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!hasFeature('email_integration') || !hasPermission('email', 'view')) return;
        fetch(EMAIL_ANALYTICS_URL)
            .then(res => { if (res?.status === 'success') setEmailSummary(res.data); })
            .catch(() => {});
    }, [refreshCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Both onboarded widgets are behind the same 'onboarded' permission.
        if (!hasFeature('customers') || !hasPermission('onboarded', 'view')) return;
        fetch(ONBOARDED_REVENUE_REPORT)
            .then(res => { if (res?.status === 'success') setOnboardedSummary(res.data); })
            .catch(() => {});
        fetch(`${ONBOARDED_AMC_EXPIRY_URL}?days=30`)
            .then(res => { if (res?.status === 'success') setExpiringAmc(res.data || []); })
            .catch(() => {});
    }, [refreshCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () => setRefreshCount(c => c + 1);

    const handleNavigateToCustomer = (customerId) => {
        navigate('/customers/customerdetails', {
            state: { formEdit: true, row: { id: customerId } },
        });
    };

    // Build projection lookup from summary (must be before early returns — hooks rules)
    const projByMonth = useMemo(() => {
        const map = {};
        (projSummary?.months || []).forEach(m => { map[m.month] = m; });
        return map;
    }, [projSummary]);

    const isManager = ["admin", "super_admin"].includes(sessionStorage.getItem("login_role"));

    if (loading) {
        return (
            <ContainerCard>
                <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 py-4 space-y-5">
                    <div className="space-y-2">
                        <Skeleton className="h-7 w-64" />
                        <Skeleton className="h-4 w-40" />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-[112px] rounded-xl" />)}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Skeleton className="h-80 rounded-2xl lg:col-span-2" />
                        <Skeleton className="h-80 rounded-2xl" />
                    </div>
                </div>
            </ContainerCard>
        );
    }

    if (!data) return null;

    const { overview, pipeline, monthly_trend, top_performers, activity_feed } = data;

    const pipelineTotal = Object.values(pipeline || {}).reduce((a, b) => a + b, 0);
    const decided = (overview.closed_won || 0) + (overview.closed_lost || 0);
    const winRate = decided ? Math.round((overview.closed_won / decided) * 100) : null;

    // Chart data for monthly trend
    const chartData = {
        labels: (monthly_trend || []).map(r => r.month),
        datasets: [{
            label: 'New Leads',
            data: (monthly_trend || []).map(r => r.count),
            backgroundColor: chartColor(1, 0.85),
            hoverBackgroundColor: chartColor(1, 1),
            borderRadius: 6,
            borderSkipped: false,
            maxBarThickness: 36,
        }],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: TOOLTIP,
        },
        scales: {
            y: axis({ beginAtZero: true, ticks: { ...TICKS, precision: 0 } }),
            x: flatAxis(),
        },
    };

    // ── forecast chart data ────────────────────────────────────────────────────
    const forecastTrendData = {
        labels: (forecastData?.monthly_trend || []).map(r => fmtMonth(r.month)),
        datasets: [
            {
                label: "Projected (Committed)",
                data: (forecastData?.monthly_trend || []).map(r => projByMonth[r.month]?.projected || 0),
                backgroundColor: chartColor(2, 0.75),
                borderRadius: 4,
                maxBarThickness: 18,
            },
            {
                label: "Actual (Closed Won)",
                data: (forecastData?.monthly_trend || []).map(r => r.actual),
                backgroundColor: chartColor(3, 0.85),
                borderRadius: 4,
                maxBarThickness: 18,
            },
            {
                label: "Forecast (Weighted)",
                data: (forecastData?.monthly_trend || []).map(r => r.forecast),
                backgroundColor: chartColor(1, 0.4),
                borderColor: chartColor(1, 0.9),
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 18,
            },
        ],
    };
    const forecastTrendOptions = {
        responsive: true,
        plugins: {
            legend: LEGEND,
            tooltip: { ...TOOLTIP, callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${formatINR(ctx.raw)}` } },
        },
        scales: {
            y: axis({ beginAtZero: true, ticks: { ...TICKS, callback: (v) => formatINR(v) } }),
            x: flatAxis(),
        },
    };
    const forecastStageData = {
        labels: (forecastData?.pipeline_by_stage || []).map(s => s.stage),
        datasets: [
            {
                label: "Pipeline Value",
                data: (forecastData?.pipeline_by_stage || []).map(s => s.pipeline_value),
                backgroundColor: chartColor(1, 0.8),
                borderRadius: 4,
                maxBarThickness: 14,
            },
            {
                label: "Weighted Forecast",
                data: (forecastData?.pipeline_by_stage || []).map(s => s.weighted_value),
                backgroundColor: chartColor(3, 0.8),
                borderRadius: 4,
                maxBarThickness: 14,
            },
        ],
    };
    const forecastStageOptions = {
        indexAxis: "y",
        responsive: true,
        plugins: {
            legend: LEGEND,
            tooltip: { ...TOOLTIP, callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${formatINR(ctx.raw)}` } },
        },
        scales: {
            x: axis({ beginAtZero: true, ticks: { ...TICKS, callback: (v) => formatINR(v) } }),
            y: flatAxis(),
        },
    };

    // ── Conversion chart data ──────────────────────────────────────────────────
    const convRepChartData = {
        labels: (conversionData?.rep_performance || []).map(r => r.name || r.emp_code),
        datasets: [{
            label: "Win Rate %",
            data: (conversionData?.rep_performance || []).map(r => r.win_rate),
            backgroundColor: chartColor(1, 0.8),
            borderRadius: 4,
            maxBarThickness: 16,
        }],
    };
    const convRepChartOptions = {
        indexAxis: "y", responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { ...TOOLTIP, callbacks: { label: (ctx) => ` Win Rate: ${ctx.raw}%` } } },
        scales: { x: axis({ beginAtZero: true, max: 100, ticks: { ...TICKS, callback: (v) => `${v}%` } }), y: flatAxis() },
    };
    const convCloseChartData = {
        labels: (conversionData?.time_to_close || []).map(b => b.bucket),
        datasets: [{
            label: "Deals",
            data: (conversionData?.time_to_close || []).map(b => b.count),
            backgroundColor: ["rgba(34,197,94,.8)", chartColor(1, 0.8), "rgba(245,158,11,.8)", "rgba(239,68,68,.75)", "rgba(239,68,68,.5)"],
            borderRadius: 6,
            maxBarThickness: 36,
        }],
    };
    const convCloseChartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { ...TOOLTIP, callbacks: { label: (ctx) => ` ${ctx.raw} deals` } } },
        scales: { y: axis({ beginAtZero: true, ticks: { ...TICKS, precision: 0 } }), x: flatAxis() },
    };

    const showEmail = hasFeature('email_integration') && emailSummary;

    // The jump bar lists only the sections this user actually has.
    const jumpLinks = [
        { id: "dash-overview", label: "Overview" },
        forecastData && { id: "dash-forecast", label: "Forecast" },
        conversionData && { id: "dash-conversion", label: "Conversion" },
        engagementData && { id: "dash-engagement", label: "Engagement" },
        showEmail && { id: "dash-email", label: "Email" },
        onboardedSummary && { id: "dash-onboarded", label: "Onboarded" },
    ].filter(Boolean);

    const jumpTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <ContainerCard>
            <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 pb-8 space-y-5">

                {/* ── header ── */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pt-3">
                    <div>
                        <p className="text-xs font-medium text-brand-600 dark:text-brand-300">
                            {dayjs().format("dddd, D MMMM YYYY")}
                        </p>
                        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white mt-1">
                            {greeting()}{firstName() ? `, ${firstName()}` : ""}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Here's how sales are tracking. Updated {dayjs().format('h:mm A')}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="self-start sm:self-auto inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                        Refresh
                    </button>
                </div>

                {/* ── jump bar ── */}
                {jumpLinks.length > 1 && (
                    <nav className="sticky top-0 z-20 -mx-2 sm:-mx-4 px-2 sm:px-4 py-2 bg-[hsl(var(--background))]/85 dark:bg-gray-900/85 backdrop-blur-md">
                        <div className="flex items-center gap-1.5 overflow-x-auto">
                            <LayoutGrid className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mr-1" />
                            {jumpLinks.map(l => (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => jumpTo(l.id)}
                                    className="flex-shrink-0 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:border-brand-300 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </nav>
                )}

                {/* What the team should focus on today, and who needs a push.
                    Managers only: anyone else would get a 403 and a toast. */}
                {isManager && <TeamToday key={refreshCount} />}

                {/* ── headline KPIs ── */}
                <div id="dash-overview" className="scroll-mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KpiCard
                        title="Total Leads"
                        value={overview.total_customers}
                        icon={Users}
                        colorClass="bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300"
                        sub="all leads, all time"
                    />
                    <KpiCard
                        title="Active"
                        value={overview.active_leads}
                        icon={Eye}
                        colorClass="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300"
                        sub="open, not closed"
                    />
                    <KpiCard
                        title="Closed Won"
                        value={overview.closed_won}
                        icon={CheckCircle2}
                        colorClass="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300"
                        sub={winRate != null ? `${winRate}% win rate (won vs lost)` : "no closed deals yet"}
                    />
                    <KpiCard
                        title="Due Today"
                        value={overview.followup_due_today}
                        icon={Calendar}
                        colorClass="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300"
                        sub={
                            overview.overdue_count
                                ? <span className="text-red-600 dark:text-red-400 font-medium">{overview.overdue_count.toLocaleString()} follow-ups overdue</span>
                                : "follow-ups · none overdue"
                        }
                    />
                </div>

                {/* ── inflow + pipeline ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <Section
                        icon={BarChart2}
                        title="Monthly new leads"
                        subtitle="Leads created each month"
                        className="lg:col-span-2"
                    >
                        {monthly_trend.length > 0 ? (
                            <div className="h-[260px]">
                                <Bar data={chartData} options={chartOptions} />
                            </div>
                        ) : (
                            <Empty text="No trend data available" />
                        )}
                    </Section>

                    <Section
                        icon={Target}
                        title="Pipeline"
                        subtitle={`${pipelineTotal.toLocaleString()} leads by stage`}
                    >
                        <div className="space-y-0.5">
                            {Object.entries(pipeline || {}).map(([stage, count]) => (
                                <PipelineBar key={stage} stage={stage} count={count} total={pipelineTotal} />
                            ))}
                            {Object.keys(pipeline || {}).length === 0 && <Empty />}
                        </div>
                    </Section>
                </div>

                {/* ── activity + quality + performers ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <Section
                        icon={Activity}
                        title="Recent activity"
                        subtitle="Latest stage changes across the team"
                        action={<LinkAction label="View all" onClick={() => navigate('/reports/timeline')} />}
                        className="lg:col-span-2"
                    >
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60 -mx-2">
                            {activity_feed.length === 0 ? (
                                <Empty text="No recent activity" />
                            ) : (
                                activity_feed.map(item => (
                                    <ActivityItem
                                        key={item.id}
                                        item={item}
                                        onNavigate={handleNavigateToCustomer}
                                    />
                                ))
                            )}
                        </div>
                    </Section>

                    <div className="space-y-5">
                        <Section icon={Flame} title="Lead quality" subtitle="Potential and outcomes">
                            <PotentialBar hot={overview.hot_leads || 0} medium={overview.medium_leads || 0} cold={overview.cold_leads || 0} />
                            <div className="grid grid-cols-3 gap-2 mt-4">
                                <MiniStat label="Closed lost" value={overview.closed_lost} />
                                <MiniStat label="Dis-qualified" value={overview.dis_qualified} />
                                <MiniStat
                                    label="Overdue"
                                    value={overview.overdue_count}
                                    tone={overview.overdue_count ? "text-red-600 dark:text-red-400" : undefined}
                                />
                            </div>
                        </Section>

                        <Section icon={Award} title="Top performers" subtitle="Ranked by closed won">
                            <div className="space-y-1 -mx-2">
                                {top_performers.length === 0 ? (
                                    <Empty text="No performers data" />
                                ) : (
                                    top_performers.map((p, i) => (
                                        <PerformerRow key={p.user_id} p={p} rank={i + 1} />
                                    ))
                                )}
                            </div>
                        </Section>
                    </div>
                </div>

                {/* ── Sales Forecast ── */}
                {forecastData && (
                    <Section
                        id="dash-forecast"
                        icon={TrendingUp}
                        title="Sales forecast"
                        subtitle={`${forecastScenario === 'best_case' ? 'Best case' : forecastScenario === 'worst_case' ? 'Worst case' : 'Most likely'} scenario · active pipeline`}
                        action={
                            <div className="flex items-center gap-2">
                                <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-900/60 p-0.5">
                                    {FORECAST_SCENARIOS.map(sc => (
                                        <button
                                            key={sc.key}
                                            type="button"
                                            onClick={() => setForecastScenario(sc.key)}
                                            className={cn(
                                                "px-3 py-1 rounded-md text-xs font-medium transition-all",
                                                forecastScenario === sc.key
                                                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                                    : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                            )}
                                        >
                                            {sc.label}
                                        </button>
                                    ))}
                                </div>
                                <LinkAction onClick={() => navigate('/reports/sales-forecast')} />
                            </div>
                        }
                    >
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <KpiCard
                                title="Pipeline Value"
                                value={formatINR(forecastData.summary?.pipeline_value)}
                                icon={DollarSign}
                                colorClass="bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300"
                            />
                            <KpiCard
                                title="Weighted Forecast"
                                value={formatINR(forecastData.summary?.weighted_forecast)}
                                icon={TrendingUp}
                                colorClass="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300"
                            />
                            <KpiCard
                                title="Closed This Month"
                                value={formatINR(forecastData.summary?.closed_this_month)}
                                icon={CheckCircle2}
                                colorClass="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300"
                                sub={`${forecastData.summary?.closed_this_month_count ?? 0} deals`}
                            />
                            <KpiCard
                                title="Open Deals"
                                value={forecastData.summary?.open_deals}
                                icon={BarChart2}
                                colorClass="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300"
                                sub="in pipeline"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Panel title="Monthly forecast vs actual">
                                {(forecastData.monthly_trend?.length ?? 0) > 0 ? (
                                    <Bar data={forecastTrendData} options={forecastTrendOptions} />
                                ) : (
                                    <Empty text="No trend data" />
                                )}
                            </Panel>
                            <Panel title="Pipeline by stage">
                                {(forecastData.pipeline_by_stage?.length ?? 0) > 0 ? (
                                    <Bar data={forecastStageData} options={forecastStageOptions} />
                                ) : (
                                    <Empty text="No pipeline data" />
                                )}
                            </Panel>
                        </div>
                    </Section>
                )}

                {/* ── Conversion Analytics ── */}
                {conversionData && (
                    <Section
                        id="dash-conversion"
                        icon={Target}
                        title="Conversion analytics"
                        subtitle="Funnel · stage conversions · rep performance · time to close"
                        action={<LinkAction onClick={() => navigate('/reports/conversion')} />}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                            <Panel icon={Target} title="Sales funnel">
                                {(conversionData.funnel?.length ?? 0) > 0 ? (
                                    <DashFunnel data={conversionData.funnel} />
                                ) : (
                                    <Empty />
                                )}
                            </Panel>

                            <Panel icon={TrendingDown} title="Stage conversion rates">
                                {(conversionData.stage_conversions?.length ?? 0) > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                                    <th className="pb-2 font-medium">From</th>
                                                    <th className="pb-2 font-medium">To</th>
                                                    <th className="pb-2 font-medium text-right">Conv %</th>
                                                    <th className="pb-2 font-medium text-right">Avg days</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                                {conversionData.stage_conversions.map(row => (
                                                    <tr key={`${row.from_stage}-${row.to_stage}`}>
                                                        <td className="py-2 text-gray-700 dark:text-gray-300 truncate max-w-[110px]">{row.from_stage}</td>
                                                        <td className="py-2 text-gray-700 dark:text-gray-300 truncate max-w-[110px]">{row.to_stage}</td>
                                                        <td className="py-2 text-right">
                                                            <span className={cn(
                                                                "inline-block min-w-[44px] text-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
                                                                row.conversion_pct >= 60 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" :
                                                                row.conversion_pct >= 35 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" :
                                                                "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                                            )}>{row.conversion_pct}%</span>
                                                        </td>
                                                        <td className="py-2 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                                                            {row.avg_days != null ? `${row.avg_days}d` : "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <Empty />
                                )}
                            </Panel>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Panel icon={Award} iconClass="text-amber-500" title="Win rate by rep">
                                {(conversionData.rep_performance?.length ?? 0) > 0 ? (
                                    <div className="h-[240px]"><Bar data={convRepChartData} options={convRepChartOptions} /></div>
                                ) : (
                                    <Empty />
                                )}
                            </Panel>
                            <Panel icon={Clock} iconClass="text-emerald-500" title="Time to close">
                                {conversionData.time_to_close?.some(b => b.count > 0) ? (
                                    <div className="h-[240px]"><Bar data={convCloseChartData} options={convCloseChartOptions} /></div>
                                ) : (
                                    <Empty text="No closed deals" />
                                )}
                            </Panel>
                        </div>
                    </Section>
                )}

                {/* ── Engagement Overview ── */}
                {engagementData && (
                    <Section
                        id="dash-engagement"
                        icon={Activity}
                        title="Engagement overview"
                        subtitle={`Avg score ${engagementData.average_score} · ${engagementData.total} active leads`}
                        action={<LinkAction onClick={() => navigate('/reports/engagement')} />}
                    >
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <KpiCard title="Hot"  value={engagementData.hot_count}  icon={Flame}     colorClass="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300"       sub="engaged" />
                                <KpiCard title="Warm" value={engagementData.warm_count} icon={Zap}       colorClass="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300" sub="engaged" />
                                <KpiCard title="Cold" value={engagementData.cold_count} icon={Snowflake} colorClass="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300"       sub="at risk" />
                            </div>

                            <Panel title="At-risk customers">
                                {engagementData.at_risk?.length > 0 ? (
                                    <div className="space-y-0.5 -mx-2">
                                        {engagementData.at_risk.map(r => (
                                            <div
                                                key={r.customer_id}
                                                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                                onClick={() => handleNavigateToCustomer(r.customer_id)}
                                            >
                                                <div className="w-8 h-8 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                                                    {initials(r.customer_name)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{r.customer_name}</p>
                                                    <p className="text-[11px] text-gray-400">{r.lead_stage}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <div className="w-20 bg-gray-200/70 dark:bg-gray-700 rounded-full h-1.5">
                                                        <div className="h-1.5 rounded-full bg-sky-400" style={{ width: `${r.score}%` }} />
                                                    </div>
                                                    <span className="text-xs font-semibold text-sky-600 dark:text-sky-300 w-7 text-right tabular-nums">{r.score}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Empty text="No at-risk customers" />
                                )}
                            </Panel>
                        </div>
                    </Section>
                )}

                {/* ── Email Activity ── */}
                {showEmail && (
                    <Section
                        id="dash-email"
                        icon={Mail}
                        title="Email activity"
                        subtitle="Tracking · open & click engagement · sender performance"
                        action={<LinkAction onClick={() => navigate('/reports/email-analytics')} />}
                    >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                            <KpiCard title="Sent"    value={emailSummary.total_sent}         icon={Send}         colorClass="bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300" />
                            <KpiCard title="Opened"  value={emailSummary.total_opened}       icon={Eye}          colorClass="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300" sub={`${emailSummary.open_rate}% rate`} />
                            <KpiCard title="Clicked" value={emailSummary.total_clicked}      icon={MousePointer} colorClass="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300" sub={`${emailSummary.click_rate}% rate`} />
                            <KpiCard title="Replied" value={emailSummary.total_replied ?? 0} icon={Reply}        colorClass="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300" />
                            <KpiCard title="Bounced" value={emailSummary.total_bounced}      icon={AlertCircle}  colorClass="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            <RateBar icon={Eye} label="Open rate" rate={emailSummary.open_rate}
                                     done={emailSummary.total_opened} total={emailSummary.total_sent} verb="opened"
                                     bar="bg-emerald-500" text="text-emerald-600 dark:text-emerald-400" />
                            <RateBar icon={MousePointer} label="Click rate" rate={emailSummary.click_rate}
                                     done={emailSummary.total_clicked} total={emailSummary.total_sent} verb="clicked"
                                     bar="bg-brand-500" text="text-brand-600 dark:text-brand-300" />
                        </div>

                        {((emailSummary.user_breakdown?.length ?? 0) > 0 || (emailSummary.template_performance?.length ?? 0) > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {(emailSummary.user_breakdown?.length ?? 0) > 0 && (
                                    <Panel icon={Users} title="Top senders">
                                        <div className="space-y-3">
                                            {emailSummary.user_breakdown.slice(0, 5).map((u, i) => {
                                                const openPct = u.sent ? Math.round(u.opened / u.sent * 100) : 0;
                                                const clickPct = u.sent ? Math.round(u.clicked / u.sent * 100) : 0;
                                                return (
                                                    <div key={i} className="flex items-center gap-3">
                                                        <div className="w-7 h-7 rounded-full bg-brand-50 dark:bg-brand-900/40 flex items-center justify-center text-[10px] font-semibold text-brand-700 dark:text-brand-300 flex-shrink-0">
                                                            {initials(u.user_name)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-xs text-gray-800 dark:text-gray-200 truncate font-medium">{u.user_name || 'Unknown'}</span>
                                                                <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2 tabular-nums">
                                                                    {u.sent} sent · <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{openPct}%</span> open · <span className="text-brand-600 dark:text-brand-300 font-semibold">{clickPct}%</span> click
                                                                </span>
                                                            </div>
                                                            <div className="h-1.5 bg-gray-200/70 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                <div className="h-full bg-brand-400 rounded-full" style={{ width: `${Math.min(openPct, 100)}%` }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Panel>
                                )}

                                {(emailSummary.template_performance?.length ?? 0) > 0 && (
                                    <Panel icon={FileText} iconClass="text-amber-500" title="Template performance">
                                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {emailSummary.template_performance.slice(0, 5).map((t, i) => {
                                                const openPct = t.sent ? Math.round(t.opened / t.sent * 100) : 0;
                                                return (
                                                    <div key={i} className="flex items-center justify-between py-2">
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                                            <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{t.template__name || 'Unnamed'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                            <span className="text-[11px] text-gray-400 tabular-nums">{t.sent} sent</span>
                                                            <span className={cn(
                                                                "text-[11px] font-semibold px-2 py-0.5 rounded-full tabular-nums",
                                                                openPct >= 50 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" :
                                                                openPct >= 25 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" :
                                                                "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                                            )}>{openPct}% open</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Panel>
                                )}
                            </div>
                        )}
                    </Section>
                )}

                {/* ── Onboarded Clients ── */}
                {onboardedSummary && (
                    <Section
                        id="dash-onboarded"
                        icon={BadgeCheck}
                        title="Onboarded clients"
                        subtitle="Contract revenue · AMC · implementation status"
                        action={<LinkAction label="View all" onClick={() => navigate('/onboarded')} />}
                    >
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <KpiCard
                                title="Total Clients"
                                value={onboardedSummary.total_clients}
                                icon={Users}
                                colorClass="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300"
                            />
                            <KpiCard
                                title="Contract Value"
                                value={formatINR(onboardedSummary.total_contract_value)}
                                icon={IndianRupee}
                                colorClass="bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300"
                            />
                            <KpiCard
                                title="AMC Value"
                                value={formatINR(onboardedSummary.total_amc_value)}
                                icon={CalendarClock}
                                colorClass="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300"
                            />
                            <KpiCard
                                title="AMC Renewal Rate"
                                value={`${onboardedSummary.renewal_rate_percent}%`}
                                icon={RotateCcw}
                                colorClass="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Panel title="Status breakdown">
                                <div className="space-y-2.5">
                                    {(onboardedSummary.status_breakdown || []).map(s => {
                                        const pct = onboardedSummary.total_clients
                                            ? Math.round((s.count / onboardedSummary.total_clients) * 100) : 0;
                                        const colorMap = {
                                            "In Progress": "bg-sky-500",
                                            "Go Live":     "bg-green-500",
                                            "Completed":   "bg-emerald-500",
                                            "Suspended":   "bg-red-500",
                                        };
                                        return (
                                            <div key={s.onboarding_status} className="flex items-center gap-3">
                                                <span className="text-xs text-gray-600 dark:text-gray-400 w-24 truncate">{s.onboarding_status}</span>
                                                <div className="flex-1 bg-gray-200/70 dark:bg-gray-700 rounded-full h-2">
                                                    <div className={cn("h-2 rounded-full transition-all", colorMap[s.onboarding_status] || "bg-gray-400")}
                                                        style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 w-8 text-right tabular-nums">{s.count}</span>
                                            </div>
                                        );
                                    })}
                                    {(onboardedSummary.status_breakdown || []).length === 0 && <Empty />}
                                </div>
                            </Panel>

                            <Panel
                                title="AMC expiring / overdue"
                                right={<LinkAction label="See all" onClick={() => navigate('/onboarded/reports/amc-tracker')} />}
                            >
                                {expiringAmc.length === 0 ? (
                                    <Empty text="No expiring AMCs" />
                                ) : (
                                    <div className="space-y-0.5 -mx-2">
                                        {expiringAmc.slice(0, 5).map(a => (
                                            <div key={a.amc_id}
                                                className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                                onClick={() => navigate(`/onboarded/${a.client_id}`)}>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{a.client_name}</p>
                                                    <p className="text-[11px] text-gray-400">{a.amc_end_date}</p>
                                                </div>
                                                <span className={cn(
                                                    "text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ml-2",
                                                    a.is_overdue
                                                        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                )}>
                                                    {a.is_overdue ? `${Math.abs(a.days_left)}d overdue` : `${a.days_left}d left`}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Panel>
                        </div>
                    </Section>
                )}
            </div>
        </ContainerCard>
    );
};

export default Dashboard;
